import React, { createContext, useState, useEffect, useRef, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry, showApiError, storeSettings } from "../utils";
import * as C from "../const";
import { setAccessToken, clearAccessToken, getCsrfToken, registerRefreshHandler, getAccessToken } from "../services/tokenService";
import { clear as clearNotifications } from "../services/notificationService";
import { WIDGET_SETTINGS_KEY } from "../pages/AnswerEngine/widgetSettings";
import { useAuthBroadcast } from "../hooks/useAuthBroadcast";
import {
  broadcastTokenRequest,
  broadcastTokenUpdated,
  broadcastTokenCleared,
  broadcastProjectSwitched,
  clearSharedTokenExpiry,
  coordinateRefresh,
} from "../services/authBroadcastService";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    const username = getLocalStorageWithExpiry("username");
    return {
      user: username
        ? {
            username,
            email: getLocalStorageWithExpiry("email"),
            firstname: getLocalStorageWithExpiry("firstname"),
            lastname: getLocalStorageWithExpiry("lastname"),
            client_id: getLocalStorageWithExpiry("client_id"),
            projects: getLocalStorageWithExpiry("projects") || [],
            role: getLocalStorageWithExpiry("role"),
          }
        : null,
      isAuthenticated: Boolean(username),
      isLoading: false,
      login: () => {},
      logout: () => {},
      switchProject: () => {},
    };
  }
  return context;
};

const AUTH_ISSUER = (process.env.REACT_APP_AUTH_ISSUER || C.DEFAULT_AUTH_ISSUER_PATH).replace(/\/+$/, "");
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || C.DEFAULT_BACKEND_URL_PATH).replace(/\/+$/, "");

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const refreshSessionRef = useRef(null);
  const logoutRef = useRef(null);
  const userRef = useRef(null);
  const tokenExpiryTimeRef = useRef(null);
  const bootstrapTimeoutRef = useRef(null);

  // Initial state hydration (non-sensitive only)
  const [user, setUser] = useState(() => {
    const username = getLocalStorageWithExpiry("username");
    if (!username) return null;
    return {
      username,
      email: getLocalStorageWithExpiry("email"),
      firstname: getLocalStorageWithExpiry("firstname"),
      lastname: getLocalStorageWithExpiry("lastname"),
      client_id: getLocalStorageWithExpiry("client_id"),
      projects: getLocalStorageWithExpiry("projects") || [],
      profile_picture: getLocalStorageWithExpiry("profile_picture"),
      role: getLocalStorageWithExpiry("role"),
      answer_engine_enabled: getLocalStorageWithExpiry("answer_engine_enabled"),
      answer_engine_project_id: getLocalStorageWithExpiry("answer_engine_project_id"),
      answer_engine_version_id: getLocalStorageWithExpiry("answer_engine_version_id"),
      answer_engine_sandbox_bkgd: getLocalStorageWithExpiry("answer_engine_sandbox_bkgd"),
    };
  });
  const [isLoading, setIsLoading] = useState(true);

  // Keep ref synchronized with current user state for async refresh handlers
  userRef.current = user;

  // Proactive Timer Management
  const clearTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (bootstrapTimeoutRef.current) {
      clearTimeout(bootstrapTimeoutRef.current);
      bootstrapTimeoutRef.current = null;
    }
  }, []);

  const getRemainingTokenLifetimeSeconds = useCallback(() => {
    if (!tokenExpiryTimeRef.current) return 0;
    return Math.max(0, Math.floor((tokenExpiryTimeRef.current - Date.now()) / 1000));
  }, []);

  const hydrateUserFromStorage = useCallback(() => {
    const username = getLocalStorageWithExpiry("username");
    if (!username) return null;

    const hydratedUser = {
      username,
      email: getLocalStorageWithExpiry("email"),
      firstname: getLocalStorageWithExpiry("firstname"),
      lastname: getLocalStorageWithExpiry("lastname"),
      client_id: getLocalStorageWithExpiry("client_id"),
      projects: getLocalStorageWithExpiry("projects") || [],
      profile_picture: getLocalStorageWithExpiry("profile_picture"),
      role: getLocalStorageWithExpiry("role"),
      answer_engine_enabled: getLocalStorageWithExpiry("answer_engine_enabled"),
      answer_engine_project_id: getLocalStorageWithExpiry("answer_engine_project_id"),
      answer_engine_version_id: getLocalStorageWithExpiry("answer_engine_version_id"),
      answer_engine_sandbox_bkgd: getLocalStorageWithExpiry("answer_engine_sandbox_bkgd"),
    };

    setUser(hydratedUser);
    return hydratedUser;
  }, []);

  // Proactive timer only; cross-tab locking/dedup now lives entirely in coordinateRefresh.
  const scheduleRefresh = useCallback((expiresInSeconds) => {
    clearTimer();

    const delay = Math.max(
      (expiresInSeconds - C.AUTH_REFRESH_BUFFER_SECONDS) * 1000,
      C.AUTH_REFRESH_MIN_DELAY_MS
    );

    refreshTimerRef.current = setTimeout(async () => {
      try {
        if (refreshSessionRef.current) {
          await refreshSessionRef.current();
        }
      } catch (err) {
        console.warn(`[Auth Sync: ${new Date().toISOString()}] Proactive refresh failed, retrying in 15s:`, err);
        scheduleRefresh(C.AUTH_REFRESH_BUFFER_SECONDS + (C.AUTH_REFRESH_RETRY_DELAY_MS / 1000));
      }
    }, delay);
  }, [clearTimer]);

  // Fetch Settings Helper
  const fetchAndStoreSettings = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/settings`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        storeSettings(data);
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
  };

  // Helper to execute the actual network refresh request
  const executeNetworkRefresh = useCallback(async (newClientId = null) => {
    try {
      const body = newClientId ? JSON.stringify({ client_id: newClientId }) : undefined;
      const headers = { "Content-Type": "application/json" };
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }

      const response = await fetch(`${AUTH_ISSUER}/refresh`, {
        method: "POST",
        headers,
        credentials: "include",
        body,
      });

      console.log(`[Auth Sync: ${new Date().toISOString()}] Requesting session refresh`);

      if (!response.ok) {
        const err = new Error("Refresh request failed.");
        err.status = response.status;
        throw err;
      }

      const data = await response.json();
      const { access_token, expires_in } = data;

      if (!access_token) {
        throw new Error("Invalid refresh payload.");
      }

      const expiresIn = expires_in || C.AUTH_DEFAULT_EXPIRY_SECONDS;
      const expiresAt = Date.now() + expiresIn * 1000;
      setAccessToken(access_token, expiresAt);

      // Sliding session expiry: Extend local storage TTLs for active user session
      if (userRef.current) {
        Object.entries(userRef.current).forEach(([key, value]) => {
          if (value !== undefined) {
            setLocalStorageWithExpiry(key, value, C.EXPIRY_TIME_IN_MINUTES);
          }
        });
      }

      tokenExpiryTimeRef.current = expiresAt;
      scheduleRefresh(expiresIn);
      broadcastTokenUpdated(access_token, expiresAt);
      return access_token;
    } catch (err) {
      clearAccessToken();
      tokenExpiryTimeRef.current = null;
      // Only the leader's own refresh knows the session is truly dead (401/403); a network
      // or 5xx failure is transient, so leave the shared marker for peers to retry against.
      if (err?.status === 401 || err?.status === 403) {
        clearSharedTokenExpiry();
      }
      throw err;
    }
  }, [scheduleRefresh]);

  // 1. Session Refresh: single-flight locally, cross-tab serialization delegated to coordinateRefresh
  const refreshSession = useCallback(async (newClientId = null) => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = coordinateRefresh(
      () => executeNetworkRefresh(newClientId),
      { allowPeerReuse: !newClientId }
    ).finally(() => {
      refreshPromiseRef.current = null;
    });

    return refreshPromiseRef.current;
  }, [executeNetworkRefresh]);

  // Register callback bridge for interceptors
  useEffect(() => {
    refreshSessionRef.current = refreshSession;
    registerRefreshHandler(refreshSession);
  }, [refreshSession]);

  // 2. User Login Flow
  const login = async (email, password) => {
    const authResponse = await fetch(`${AUTH_ISSUER}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    const authData = await authResponse.json();
    if (!authResponse.ok) {
      throw new Error(authData.detail || "Authentication failed.");
    }

    const { access_token, expires_in } = authData;
    const expiresIn = expires_in || C.AUTH_DEFAULT_EXPIRY_SECONDS;
    const expiresAt = Date.now() + expiresIn * 1000;
    setAccessToken(access_token, expiresAt);

    // Fetch user details
    const response = await fetch(`${BACKEND_URL}/api/v2/user/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${access_token}`,
      },
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || "User profile fetch failed.");
    }

    const userData = {
      username: data.username,
      email: data.email || data.username,
      firstname: data.firstname,
      lastname: data.lastname,
      client_id: data.client_id,
      projects: data.projects,
      profile_picture: data.profile_picture,
      role: data.role,
      answer_engine_enabled: data.answer_engine_enabled,
      answer_engine_project_id: data.answer_engine_project_id,
      answer_engine_version_id: data.answer_engine_version_id || C.DEFAULT_ANSWER_ENGINE_VERSION,
      answer_engine_sandbox_bkgd: data.answer_engine_sandbox_bkgd,
    };

    // Keep user state populated locally
    Object.entries(userData).forEach(([key, value]) => {
      setLocalStorageWithExpiry(key, value, C.EXPIRY_TIME_IN_MINUTES);
    });

    await fetchAndStoreSettings(access_token);
    setUser(userData);
    tokenExpiryTimeRef.current = expiresAt;
    scheduleRefresh(expiresIn);
    broadcastTokenUpdated(access_token, expiresAt);

    return userData;
  };

  // 3. Project Switching Flow
  const switchProject = async (newClientId) => {
    try {
      const token = await refreshSession(newClientId);
      const response = await fetch(`${BACKEND_URL}/api/v2/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error("Project transition failed on backend.");
      }

      const userData = {
        username: data.username,
        email: data.email || data.username,
        firstname: data.firstname,
        lastname: data.lastname,
        client_id: data.client_id,
        projects: data.projects,
        profile_picture: data.profile_picture,
        role: data.role,
        answer_engine_enabled: data.answer_engine_enabled,
        answer_engine_project_id: data.answer_engine_project_id,
        answer_engine_version_id: data.answer_engine_version_id || C.DEFAULT_ANSWER_ENGINE_VERSION,
        answer_engine_sandbox_bkgd: data.answer_engine_sandbox_bkgd,
      };

      Object.entries(userData).forEach(([key, value]) => {
        setLocalStorageWithExpiry(key, value, C.EXPIRY_TIME_IN_MINUTES);
      });

      // Clear assistant session so conversations do not bleed across client projects
      try {
        localStorage.removeItem(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY);
      } catch (e) {
        // ignore
      }

      await fetchAndStoreSettings(token);
      setUser(userData);
      
      // Update answer engine version in localStorage widget settings
      if (data.answer_engine_version_id !== undefined) {
        try {
          const raw = localStorage.getItem(WIDGET_SETTINGS_KEY);
          const parsed = raw ? JSON.parse(raw) : {};
          localStorage.setItem(
            WIDGET_SETTINGS_KEY,
            JSON.stringify({
              ...parsed,
              versionId: data.answer_engine_version_id || "",
            }),
          );
        } catch (e) {
          console.warn("Failed to update widget settings in localStorage:", e);
        }
      }

      // Update other tabs first
      broadcastProjectSwitched(newClientId);

      // Force instant window reload to reset React Query caches & components smoothly
      window.location.reload();
    } catch (err) {
      showApiError(err.message || "Failed to switch projects.");
      // Atomic Rollback: Revert the backend token & local token back to the current active project
      if (user && user.client_id) {
        try {
          await refreshSession(user.client_id);
        } catch (rollbackErr) {
          console.error("Atomic rollback failed, logging out:", rollbackErr);
          logout();
        }
      } else {
        logout();
      }
      throw err;
    }
  };

  // 4. Logout Flow
  const logout = useCallback(() => {
    const token = getAccessToken();
    if (token) {
      fetch(`${AUTH_ISSUER}/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      }).catch((err) => console.warn("Local logout proceeded regardless of backend:", err));
    }

    clearAccessToken();
    clearNotifications();
    tokenExpiryTimeRef.current = null;
    clearTimer();
    setUser(null);
    broadcastTokenCleared();

    Object.keys(localStorage).forEach((key) => {
      if (!key.startsWith(C.SESSION_PRESERVE_PREFIX) && key !== C.I18N_LOCALE_KEY) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach((key) => {
      if (!key.startsWith(C.SESSION_PRESERVE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });

    navigate("/login");
  }, [clearTimer, navigate]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Multi-tab synchronization hook
  useAuthBroadcast({
    onTokenRequested: () => {
      const token = getAccessToken();
      const remaining = getRemainingTokenLifetimeSeconds();

      if (token && remaining > C.TOKEN_SHARE_MIN_TTL_SECONDS) {
        broadcastTokenUpdated(token, tokenExpiryTimeRef.current);
      }
    },
    onTokenUpdated: ({ accessToken, expiresAt }) => {
      setAccessToken(accessToken, expiresAt);
      tokenExpiryTimeRef.current = expiresAt;
      hydrateUserFromStorage();

      if (bootstrapTimeoutRef.current) {
        clearTimeout(bootstrapTimeoutRef.current);
        bootstrapTimeoutRef.current = null;
      }

      setIsLoading(false);
      scheduleRefresh(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    },
    onTokenCleared: () => {
      clearAccessToken();
      clearNotifications();
      tokenExpiryTimeRef.current = null;
      clearTimer();
      setUser(null);
      setIsLoading(false);
      navigate("/login");
    },
    onProjectSwitched: () => {
      window.location.reload();
    }
  }, [
    navigate,
    scheduleRefresh,
    clearTimer,
    hydrateUserFromStorage,
    getRemainingTokenLifetimeSeconds,
  ]);

  // Initial silent re-authentication boot
  useEffect(() => {
    const initializeSession = async () => {
      const username = getLocalStorageWithExpiry("username");
      if (!username) {
        setIsLoading(false);
        return;
      }

      hydrateUserFromStorage();

      if (getAccessToken()) {
        setIsLoading(false);
        return;
      }

      broadcastTokenRequest();

      bootstrapTimeoutRef.current = setTimeout(async () => {
        if (getAccessToken()) {
          setIsLoading(false);
          return;
        }

        try {
          await refreshSession();
        } catch (err) {
          console.warn("Silent re-auth failed, logging out:", err);
          logout();
        } finally {
          setIsLoading(false);
          bootstrapTimeoutRef.current = null;
        }
      }, C.PEER_SYNC_WAIT_MS);
    };

    initializeSession();

    return () => {
      if (bootstrapTimeoutRef.current) {
        clearTimeout(bootstrapTimeoutRef.current);
        bootstrapTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to global logout-redirect events from interceptors
  useEffect(() => {
    const handleRedirect = () => logout();
    window.addEventListener(C.AUTH_LOGOUT_REDIRECT_EVENT, handleRedirect);
    return () => window.removeEventListener(C.AUTH_LOGOUT_REDIRECT_EVENT, handleRedirect);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!(user && getAccessToken()),
        login,
        refreshSession,
        switchProject,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
